import type { ViewIndependentData, ViewRecordPool } from '@lighthouse/core'
import {
    type DataSourceAbstract,
    type DataSourceIdentity,
    type EnvironmentAbstract,
    EnvironmentStruct,
    type RecordIdentity,
    type RecordLikeProtocol
} from '@lighthouse/core'
import {
    filterRecordsInPool,
    findRecordByCondition,
    findRecordInPool,
    getMainTableRecordId,
    type RecordPoolMap,
    USER_DATASOURCE
} from '@lighthouse/shared'
import shallowEqual from 'fast-deep-equal'
import type { Draft } from 'immer'
import { atom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { atomWithImmer } from 'jotai-immer'
import { find } from 'rambda'

import { commonRoleIds } from '../application/constants'
import { appRoleAtom, currentEnvIdAtom } from '../application/state'
import { applyDraftPayload } from '../utils/applyDraftPayload'

// export const dataSourceListIdsAtom = atomWithImmer<string[]>([])
export const dataSourcePoolAtom = atomWithImmer<DataSourceAbstract[]>([])

/** recordPool 使用 Map 结构，key 为 `${dsId}-${recordId}` */
export const recordPoolAtom = atomWithImmer<RecordPoolMap>(new Map())
export const currentDataSourceIdAtom = atom('')
// export const dataSourceEnvIdAtom = atomWithImmer<string>('')
// export const dataSourceUserListAtom = atomWithImmer<UserOption[]>([])
// export const dataSourceRoleListAtom = atomWithImmer<RoleProtocols[]>([])
// export const dataSourceDepartmentListAtom = atomWithImmer<DeptOption[]>([])
export const dataSourceEnvAtom = atomWithImmer<EnvironmentAbstract>({ ...EnvironmentStruct() })

export const viewIndependentDataAtom = atomWithImmer<ViewRecordPool>({})

export const dataSourceAtomFamily = atomFamily((params: DataSourceIdentity) => {
    const { appId, envId, dsId } = params
    return atom(
        get => get(dataSourcePoolAtom).find(item => item.appId === appId && item.envId === envId && item.id === dsId),
        (_, set, payload: DataSourceAbstract | undefined | ((draft: Draft<DataSourceAbstract | undefined>) => void)) =>
            set(dataSourcePoolAtom, draft => {
                const dataSource = draft.find(item => item.id === dsId && item.appId === appId && item.envId === envId)
                applyDraftPayload(dataSource, payload)
            })
    )
}, shallowEqual)

export const recordAtomFamily = atomFamily((params: RecordIdentity) => {
    // 解决连接表问题
    const { appId, envId, dsId, recordId } = params
    return atom(
        get => {
            const pool = get(recordPoolAtom)
            // 直接 O(1) 查找
            const record = findRecordInPool(pool, dsId, recordId)
            if (record && record.appId === appId && record.envId === envId) {
                return record
            }
            return undefined
        },
        (_, set, payload: RecordLikeProtocol | ((draft: Draft<RecordLikeProtocol>) => void)) =>
            set(recordPoolAtom, draft => {
                const mainRecordId = getMainTableRecordId(recordId)
                // 遍历找到所有需要更新的记录
                for (const record of draft.values()) {
                    if (record.appId === appId && getMainTableRecordId(record.id) === mainRecordId) {
                        applyDraftPayload(record, payload)
                    }
                }
            })
    )
}, shallowEqual)

export const dataSourceListAtomFamily = atomFamily((params: { appId: string; envId: string }) => {
    const { appId, envId } = params
    return atom(get => get(dataSourcePoolAtom).filter(item => item.appId === appId && item.envId === envId))
}, shallowEqual)

export const recordListAtomFamily = atomFamily((params: { appId: string; dsId: string }) => {
    const { appId, dsId } = params
    return atom(get => {
        const pool = get(recordPoolAtom)
        return filterRecordsInPool(pool, item => item.appId === appId && item.dsId === dsId)
    })
}, shallowEqual)

export const userRecordAtom = atom(get => {
    const pool = get(recordPoolAtom)
    const envId = get(currentEnvIdAtom)
    const userId = get(appRoleAtom)
    if (commonRoleIds.includes(userId)) {
        return
    }
    return findRecordByCondition(
        pool,
        record => record?.content?.['ID']?.value === userId && Boolean(userId) && record.dsId === USER_DATASOURCE && record.envId === envId
    )
})

export const userDataSourceAtom = atom(get => {
    const dataSourceList = get(dataSourcePoolAtom)
    // const envId = get(dataSourceEnvIdAtom)
    const envId = get(currentEnvIdAtom)
    return find(ds => ds.id === USER_DATASOURCE && ds.envId === envId, dataSourceList)
})

/**
 * 已将视图block的数据拆分出去，现在只有数据源使用这个atom
 */
export const dsDependentDataAtomFamily = atomFamily((payload: { dsId: string; viewId: string }) => {
    const { dsId, viewId } = payload
    return atom(
        get => {
            const ViewRecordPool = get(viewIndependentDataAtom)
            return ViewRecordPool?.[viewId]
        },
        (_, set, payload: ViewIndependentData | ((draft: Draft<ViewIndependentData>) => void)) =>
            set(viewIndependentDataAtom, draft => {
                if (typeof payload === 'function') {
                    if (!draft[viewId]) {
                        draft[viewId] = {
                            dsId,
                            recordPoints: [],
                            currentPage: 1,
                            rowTotal: 0,
                            search: ''
                        }
                    }
                    applyDraftPayload(draft[viewId], payload)
                    return
                }
                // applyDraftPayload(draft[viewId], payload)
                draft[viewId] = payload
            })
    )
}, shallowEqual)

/**
 * 视图block的依赖数据，单独管理，需要stackId隔离
 */
export const viewIndependentDataAtomFamily = atomFamily((payload: { stackId: string; viewId: string; dsId: string }) => {
    const { dsId } = payload
    return atomWithImmer<ViewIndependentData>({
        dsId,
        recordPoints: [],
        currentPage: 1,
        rowTotal: 0,
        search: ''
    })
}, shallowEqual)
